/**
 * One validated source, at a glance.
 *
 * Two of these sit SIDE BY SIDE on the Overview tab rather than behind a tab
 * strip. The chains validate different bodies of data and can legitimately
 * disagree — that disagreement is the single most useful thing the panel has to
 * say, and a tab strip hides half of it behind a click. Nothing here merges,
 * averages or ranks the two.
 */

import { ArrowRight, FileText } from 'lucide-react';
import { Badge } from '../../ui/Badge';
import type { ValidationChain } from '../../../types/dataQuality';
import AiStatusChip from './AiStatusChip';
import QualityRing from './QualityRing';
import SeverityBar from './SeverityBar';
import TrendPill from './TrendPill';
import { chainDescription, chainLabel, pdfProvenance } from './dqSelectors';
import { AI_STATUS_TOKENS, STATUS_TOKENS } from './dqTokens';

export interface SourceScorecardProps {
  chain: ValidationChain;
  /** Renders the whole card as a button into the console. */
  onOpen?: () => void;
}

function MicroStat({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <p className="font-mono text-sm font-semibold tabular-nums text-neutral-900">{value}</p>
      <p className="text-[11px] uppercase tracking-wide text-neutral-400">{label}</p>
    </div>
  );
}

export default function SourceScorecard({ chain, onOpen }: SourceScorecardProps) {
  const { summary } = chain;
  const status = STATUS_TOKENS[summary.status] ?? STATUS_TOKENS.warnings;
  const StatusIcon = status.icon;
  const provenance = pdfProvenance(chain);
  const aiTone = summary.ai_status ? AI_STATUS_TOKENS[summary.ai_status]?.tone : undefined;

  const body = (
    <>
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <StatusIcon className={`h-4 w-4 shrink-0 ${status.text}`} aria-hidden="true" />
            <span className="text-sm font-semibold text-neutral-900">{chainLabel(chain)}</span>
            <Badge variant={status.badge} size="sm">
              {status.label}
            </Badge>
          </div>

          <p className="mt-0.5 text-xs text-neutral-500">{chainDescription(chain)}</p>

          {/* PDF-only: which document, uploaded when. Rendered only when the
              backend supplied both — a placeholder would name a document the
              run cannot vouch for. */}
          {provenance && (
            <p className="mt-1.5 flex items-center gap-1.5 text-xs text-neutral-500">
              <FileText className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {provenance}
            </p>
          )}
        </div>

        <QualityRing summary={summary} size={96} />
      </div>

      {/* The backend's own sentence, rendered as given — never rebuilt from the
          counts, which would let the panel disagree with itself. */}
      <p className="mt-3 text-sm leading-relaxed text-neutral-700">{summary.headline}</p>

      <div className="mt-3 space-y-2">
        <SeverityBar summary={summary} />
        <div className="flex flex-wrap items-center gap-2">
          <TrendPill chain={chain} />
          {/* A degraded review gets its whole sentence here, not just a chip.
              "Review unavailable" alone does not tell the reader that the score
              beside it has had less scrutiny than a reviewed one, and making
              them open a tab to find that out is how the old panel's inline
              advisory got lost. Healthy states stay compact. */}
          <AiStatusChip
            summary={summary}
            variant={aiTone === 'degraded' ? 'full' : 'chip'}
          />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3 border-t border-neutral-100 pt-3">
        <MicroStat label="checks run" value={summary.checks_run ?? 0} />
        <MicroStat label="periods" value={summary.periods_checked ?? 0} />
        <MicroStat label="distinct issues" value={summary.issue_count ?? 0} />
      </div>
    </>
  );

  if (!onOpen) {
    return <div className="rounded-xl border border-neutral-200 p-4">{body}</div>;
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Open the data quality console for ${chainLabel(chain)}`}
      className="group w-full rounded-xl border border-neutral-200 p-4 text-left transition-colors hover:border-primary-300 hover:bg-primary-50/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
    >
      {body}
      <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary-600 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
        Open in console
        <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
      </span>
    </button>
  );
}
